import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  IconButton,
  Flex,
  Text,
  Card,
  Grid,
  Callout,
  Tabs,
  Badge,
} from "@radix-ui/themes";
import {
  Plus,
  Filter,
  Eye,
  Pause,
  Play,
  Copy,
  Check,
  TrendingUp,
  Users,
  CreditCard,
  AlertTriangle,
  Wrench,
  Trash2,
  RefreshCw,
  Ban,
  UserCog,
} from "lucide-react";
import toast from "react-hot-toast";
import PageTemplate from "@/components/page-template/PageTemplate";
import DataGrid, { DataGridColumn } from "@/components/ui/DataGrid";
import PaymentsPage from "./Payments";
import DropdownMenu from "@/components/ui/DropdownMenu";
import SubscriptionPlansList from "@/components/anythinkpay/SubscriptionPlansList";
import {
  Subscription,
  SUBSCRIPTION_STATUSES,
  PAYMENT_STATUSES,
  formatCurrency,
  formatDate,
  formatRelativeDate,
  getNextBillingDate,
  isSubscriptionActive,
  isSubscriptionScheduledCancel,
  providerLabel,
  providerBadgeColor,
} from "@/types/breeze/AnythinkPay";
import { useAuth } from "@/providers/AuthProvider";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import {
  useAnythinkPaySubscriptions,
  useAnythinkPaySubscriptionsByStatus,
  useCancelAnythinkPaySubscription,
  useResumeAnythinkPaySubscription,
  useAdminDeleteSubscription,
  useAdminSetSubscriptionStatus,
  useAdminResyncSubscription,
} from "@/hooks/useAnythinkPaySubscriptions";
import { SubscriptionStatusBadge } from "@/components/subscriptions/SubscriptionStatusBadge";
import { BillingIntervalDisplay } from "@/components/subscriptions/BillingIntervalDisplay";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import ErrorCard from "@/components/ui/ErrorCard";

export default function SubscriptionsPage() {
  const { safeNavigate } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === "plans" || tabParam === "payments"
      ? tabParam
      : "subscribers";
  const setActiveTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "plans" || tab === "payments") {
      next.set("tab", tab);
    } else {
      next.delete("tab");
    }
    setSearchParams(next, { replace: true });
  };
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(25);
  const [filter, setFilter] = useState("");
  const [copiedSubscriptionId, setCopiedSubscriptionId] = useState<
    string | null
  >(null);
  const [paymentsStatusFilter, setPaymentsStatusFilter] = useState<string>("all");
  const [paymentsProviderFilter, setPaymentsProviderFilter] = useState<string>("all");
  const [paymentsCreateOpen, setPaymentsCreateOpen] = useState(false);

  // Fetch subscriptions with pagination
  const {
    data: allSubscriptions,
    isLoading: isLoadingAll,
    error: errorAll,
  } = useAnythinkPaySubscriptions(currentPage, currentPageSize, filter);

  const {
    data: statusFilteredSubscriptions,
    isLoading: isLoadingFiltered,
    error: errorFiltered,
  } = useAnythinkPaySubscriptionsByStatus(
    statusFilter,
    currentPage,
    currentPageSize,
    filter,
  );

  // Determine which data to show
  const subscriptions = useMemo(() => {
    if (statusFilter === "all") {
      return allSubscriptions;
    }
    return statusFilteredSubscriptions;
  }, [statusFilter, allSubscriptions, statusFilteredSubscriptions]);

  const isLoading = statusFilter === "all" ? isLoadingAll : isLoadingFiltered;
  const error = statusFilter === "all" ? errorAll : errorFiltered;

  // Provider may be missing on subscriptions created before the column existed; the legacy
  // `type === "web"` flag distinguished those Stripe rows. Map either to the shared label.
  const sourceKey = (subscription: Subscription): string =>
    subscription.provider ?? (subscription.type === "web" ? "stripe" : subscription.type);

  // Mutations
  const cancelSubscription = useCancelAnythinkPaySubscription();
  const resumeSubscription = useResumeAnythinkPaySubscription();
  const adminDelete = useAdminDeleteSubscription();
  const adminSetStatus = useAdminSetSubscriptionStatus();
  const adminResync = useAdminResyncSubscription();

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!subscriptions?.items) {
      return {
        total: 0,
        active: 0,
        pastDue: 0,
        scheduledCancel: 0,
        mrr: 0,
        arr: 0,
      };
    }

    let mrr = 0;
    let arr = 0;

    subscriptions.items.forEach((sub) => {
      if (isSubscriptionActive(sub)) {
        if (sub.billing_interval === "month") {
          mrr += sub.amount * sub.interval_count;
        } else if (sub.billing_interval === "year") {
          mrr += sub.amount / (12 / sub.interval_count);
        } else if (sub.billing_interval === "week") {
          mrr += (sub.amount * sub.interval_count * 52) / 12;
        } else if (sub.billing_interval === "day") {
          mrr += (sub.amount * sub.interval_count * 365) / 12;
        }
      }
    });

    arr = mrr * 12;

    return {
      total: subscriptions.total_items || subscriptions.items.length,
      active: subscriptions.items.filter((s) => isSubscriptionActive(s)).length,
      pastDue: subscriptions.items.filter((s) => s.status === "past_due")
        .length,
      scheduledCancel: subscriptions.items.filter((s) =>
        isSubscriptionScheduledCancel(s),
      ).length,
      mrr,
      arr,
    };
  }, [subscriptions]);

  const handleCreate = () => {
    safeNavigate(`/integrations/anythinkpay/subscriptions/create`);
  };

  const handleCreatePlan = () => {
    safeNavigate(`/integrations/anythinkpay/subscriptions/plans/create`);
  };

  const handleViewDetails = (subscription: Subscription) => {
    safeNavigate(`/integrations/anythinkpay/subscriptions/${subscription.id}`);
  };

  const handleCancel = async (subscription: Subscription) => {
    const confirmed = await confirm({
      title: "Cancel Subscription",
      message: `Are you sure you want to cancel the subscription "${subscription.name}"? The subscription will remain active until the end of the current billing period.`,
      confirmText: "Cancel Subscription",
      cancelText: "Keep Active",
      variant: "warning",
    });

    if (confirmed) {
      cancelSubscription.mutate(subscription.id);
    }
  };

  const handleResume = async (subscription: Subscription) => {
    const confirmed = await confirm({
      title: "Resume Subscription",
      message: `Are you sure you want to resume the subscription "${subscription.name}"? It will continue billing at the end of the current period.`,
      confirmText: "Resume",
      cancelText: "Cancel",
      variant: "info",
    });

    if (confirmed) {
      resumeSubscription.mutate(subscription.id);
    }
  };

  const handleAdminDelete = async (subscription: Subscription) => {
    const confirmed = await confirm({
      title: "Delete subscription",
      message: `Permanently remove "${subscription.name}" and its user links from Anythink? A live Stripe subscription is cancelled at Stripe immediately, so the customer stops being billed. A live App Store subscription can't be deleted until the customer cancels it in the App Store. This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Keep",
      variant: "danger",
    });
    if (confirmed) adminDelete.mutate(subscription.id);
  };

  const handleForceExpire = async (subscription: Subscription) => {
    const confirmed = await confirm({
      title: "Force-expire subscription",
      message: `Mark "${subscription.name}" as expired and end access immediately? A live Stripe subscription is cancelled at Stripe so billing stops too; a live App Store subscription must be cancelled by the customer in the App Store first. The record is kept for history.`,
      confirmText: "Force-expire",
      cancelText: "Cancel",
      variant: "warning",
    });
    if (confirmed)
      adminSetStatus.mutate({
        id: subscription.id,
        status: "expired",
        clearPeriod: true,
      });
  };

  const handleResync = (subscription: Subscription) => {
    adminResync.mutate(subscription.id);
  };

  const handleCopySubscriptionLink = (subscription: Subscription) => {
    if (subscription.checkout_url) {
      navigator.clipboard.writeText(subscription.checkout_url);
      setCopiedSubscriptionId(subscription.id);
      toast.success("Subscription link copied to clipboard!");
      setTimeout(() => setCopiedSubscriptionId(null), 2000);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setCurrentPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  // Define columns for DataGrid
  const columns: DataGridColumn<Subscription>[] = [
    {
      field: "name",
      header: "Subscription",
      cellRenderer: (subscription) => (
        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">
            {subscription.name}
          </Text>
          {subscription.description && (
            <Text size="1" color="gray" style={{ maxWidth: "300px" }}>
              {subscription.description}
            </Text>
          )}
        </Flex>
      ),
    },
    {
      field: "customer_email",
      header: "Customer",
      cellRenderer: (subscription) => {
        if (!subscription.customer_email && !subscription.customer_name) {
          return (
            <Text size="2" color="gray">
              —
            </Text>
          );
        }
        return (
          <Flex direction="column" gap="2">
            {subscription.customer_name && (
              <Text size="2" weight="medium">
                {subscription.customer_name}
              </Text>
            )}
            {subscription.customer_email && (
              <Text size="1" color="gray">
                {subscription.customer_email}
              </Text>
            )}
          </Flex>
        );
      },
      width: 180,
    },
    {
      field: "status",
      header: "Status",
      cellRenderer: (subscription) => (
        <SubscriptionStatusBadge status={subscription.status} showIcon />
      ),
      width: 140,
    },
    {
      field: "amount",
      header: "Amount",
      cellRenderer: (subscription) => (
        <Flex direction="row" gap="2" align="center">
          <Text size="2" weight="bold">
            {formatCurrency(subscription.amount, subscription.currency)}
          </Text>
          <BillingIntervalDisplay
            interval={subscription.billing_interval}
            intervalCount={subscription.interval_count}
          />
        </Flex>
      ),
      width: 150,
    },
    {
      field: "current_period_ends_at",
      header: "Next Billing",
      valueGetter: (subscription) => {
        const nextBilling = getNextBillingDate(subscription);
        if (!nextBilling) return "—";
        return formatRelativeDate(nextBilling);
      },
      cellRenderer: (subscription) => {
        const nextBilling = getNextBillingDate(subscription);
        if (!nextBilling)
          return (
            <Text size="2" color="gray">
              —
            </Text>
          );
        return (
          <Flex direction="column">
            <Text size="2">{formatRelativeDate(nextBilling)}</Text>
            <Text size="1" color="gray">
              {formatDate(nextBilling)}
            </Text>
          </Flex>
        );
      },
      width: 150,
    },
    {
      field: "auto_cancel_at",
      header: "Cancellation",
      cellRenderer: (subscription) => {
        if (!subscription.auto_cancel_at) {
          return (
            <Text size="2" color="gray">
              —
            </Text>
          );
        }
        return (
          <Flex direction="column">
            <Text size="2">
              {formatRelativeDate(subscription.auto_cancel_at)}
            </Text>
            <Text size="1" color="gray">
              {formatDate(subscription.auto_cancel_at)}
            </Text>
          </Flex>
        );
      },
      width: 150,
    },
    {
      field: "provider",
      header: "Source",
      valueGetter: (subscription) => providerLabel(sourceKey(subscription)),
      cellRenderer: (subscription) => (
        <Badge color={providerBadgeColor(sourceKey(subscription))} variant="soft">
          {providerLabel(sourceKey(subscription))}
        </Badge>
      ),
      width: 110,
    },
  ];

  if (error) {
    return (
      <PageTemplate
        title="AnythinkPay Subscriptions"
        description="Manage customer subscriptions through AnythinkPay"
      >
        <ErrorCard error={error.message} />
      </PageTemplate>
    );
  }

  const rightContent =
    activeTab === "payments" ? (
      <Flex gap="2">
        <DropdownMenu
          icon={
            <Button variant="outline" size="2">
              <Filter size={16} />
              Source:{" "}
              {paymentsProviderFilter === "all"
                ? "All"
                : providerLabel(paymentsProviderFilter)}
            </Button>
          }
          items={[
            { label: "All sources", onClick: () => setPaymentsProviderFilter("all") },
            { label: "Stripe", onClick: () => setPaymentsProviderFilter("stripe") },
            { label: "Apple", onClick: () => setPaymentsProviderFilter("apple") },
            { label: "Google Play", onClick: () => setPaymentsProviderFilter("google") },
          ]}
        />
        <DropdownMenu
          icon={
            <Button variant="outline" size="2">
              <Filter size={16} />
              Status:{" "}
              {paymentsStatusFilter === "all"
                ? "All"
                : PAYMENT_STATUSES.find((s) => s.value === paymentsStatusFilter)
                    ?.label || paymentsStatusFilter}
            </Button>
          }
          items={[
            { label: "All Payments", onClick: () => setPaymentsStatusFilter("all") },
            ...PAYMENT_STATUSES.map((status) => ({
              label: status.label,
              onClick: () => setPaymentsStatusFilter(status.value),
            })),
          ]}
        />
        <Button onClick={() => setPaymentsCreateOpen(true)}>
          <Plus size={16} />
          Create Payment
        </Button>
      </Flex>
    ) : activeTab === "plans" ? (
      <Flex gap="2">
        <Button onClick={handleCreatePlan}>
          <Plus size={16} />
          New plan
        </Button>
      </Flex>
    ) : (
      <Flex gap="2">
        <DropdownMenu
          icon={
            <Button variant="outline" size="2">
              <Filter size={16} />
              Filter:{" "}
              {statusFilter === "all"
                ? "All"
                : SUBSCRIPTION_STATUSES.find((s) => s.value === statusFilter)
                    ?.label || statusFilter}
            </Button>
          }
          items={[
            {
              label: "All Subscriptions",
              onClick: () => setStatusFilter("all"),
            },
            ...SUBSCRIPTION_STATUSES.map((status) => ({
              label: status.label,
              onClick: () => setStatusFilter(status.value),
            })),
          ]}
        />
        <Button onClick={handleCreate}>
          <Plus size={16} />
          Add Subscriber
        </Button>
      </Flex>
    );

  return (
    <PageTemplate
      title="AnythinkPay Subscriptions"
      description="Manage customer subscriptions and the plans they can subscribe to."
      rightContent={rightContent}
    >
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List mb="4">
          <Tabs.Trigger value="subscribers">
            <Flex align="center" gap="2">
              <Users size={14} />
              Subscriptions
            </Flex>
          </Tabs.Trigger>
          <Tabs.Trigger value="payments">
            <Flex align="center" gap="2">
              <CreditCard size={14} />
              Payments
            </Flex>
          </Tabs.Trigger>
          <Tabs.Trigger value="plans">
            <Flex align="center" gap="2">
              <TrendingUp size={14} />
              Plans
            </Flex>
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="payments">
          <PaymentsPage
            chrome="tab"
            statusFilter={paymentsStatusFilter}
            providerFilter={paymentsProviderFilter}
            isCreateOpen={paymentsCreateOpen}
            onCreateOpenChange={setPaymentsCreateOpen}
          />
        </Tabs.Content>

        <Tabs.Content value="plans">
          <SubscriptionPlansList showHeader={false} />
        </Tabs.Content>

        <Tabs.Content value="subscribers">
      <Flex direction="column" gap="4">
        {/* Summary Cards */}
        {subscriptions?.items && subscriptions.items.length > 0 && (
          <Grid columns="4" gap="3">
            <Card>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <Users size={20} color="var(--gray-9)" />
                  <Text size="2" color="gray">
                    Total Subscriptions
                  </Text>
                </Flex>
                <Text size="5" weight="bold">
                  {summaryStats.total}
                </Text>
              </Flex>
            </Card>

            <Card>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <TrendingUp size={20} color="var(--green-9)" />
                  <Text size="2" color="gray">
                    Active
                  </Text>
                </Flex>
                <Text size="5" weight="bold" color="green">
                  {summaryStats.active}
                </Text>
              </Flex>
            </Card>

            <Card>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <TrendingUp size={20} color="var(--blue-9)" />
                  <Text size="2" color="gray">
                    Monthly Recurring Revenue
                  </Text>
                </Flex>
                <Text size="5" weight="bold">
                  {formatCurrency(summaryStats.mrr, "gbp")}
                </Text>
                <Text size="1" color="gray">
                  {formatCurrency(summaryStats.arr, "gbp")}/year
                </Text>
              </Flex>
            </Card>

            <Card>
              <Flex direction="column" gap="2">
                <Flex align="center" gap="2">
                  <AlertTriangle size={20} color="var(--orange-9)" />
                  <Text size="2" color="gray">
                    Issues
                  </Text>
                </Flex>
                <Flex direction="column" gap="2">
                  {summaryStats.pastDue > 0 && (
                    <Text size="2" color="orange">
                      {summaryStats.pastDue} Past Due
                    </Text>
                  )}
                  {summaryStats.scheduledCancel > 0 && (
                    <Text size="2" color="orange">
                      {summaryStats.scheduledCancel} Canceling
                    </Text>
                  )}
                  {summaryStats.pastDue === 0 &&
                    summaryStats.scheduledCancel === 0 && (
                      <Text size="2" color="gray">
                        None
                      </Text>
                    )}
                </Flex>
              </Flex>
            </Card>
          </Grid>
        )}

        {/* Warnings */}
        {summaryStats.pastDue > 0 && (
          <Callout.Root color="orange">
            <Callout.Icon>
              <AlertTriangle size={16} />
            </Callout.Icon>
            <Callout.Text>
              {summaryStats.pastDue} subscription
              {summaryStats.pastDue > 1 ? "s are" : " is"} past due. Please
              update payment methods to avoid cancellation.
            </Callout.Text>
          </Callout.Root>
        )}

        {summaryStats.scheduledCancel > 0 && (
          <Callout.Root color="blue">
            <Callout.Icon>
              <AlertTriangle size={16} />
            </Callout.Icon>
            <Callout.Text>
              {summaryStats.scheduledCancel} subscription
              {summaryStats.scheduledCancel > 1 ? "s are" : " is"} scheduled to
              cancel at the end of the billing period.
            </Callout.Text>
          </Callout.Root>
        )}

        {/* Data Grid */}
        {isLoading ? (
          <LoadingScreen fullPage={false} />
        ) : (
          <DataGrid
            data={subscriptions}
            columns={columns}
            storageKey="anythinkpay_subs"
            isLoading={isLoading}
            currentPage={currentPage}
            currentPageSize={currentPageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onFilterChange={handleFilterChange}
            rowSelection="none"
            actions={(subscription: Subscription) => (
              <Flex gap="1">
                <IconButton
                  variant="ghost"
                  size="1"
                  onClick={() => handleViewDetails(subscription)}
                  title="View Details"
                >
                  <Eye size={16} />
                </IconButton>
                {subscription.checkout_url && (
                  <IconButton
                    variant="ghost"
                    size="1"
                    onClick={() => handleCopySubscriptionLink(subscription)}
                    title="Copy Subscription Link"
                  >
                    {copiedSubscriptionId === subscription.id ? (
                      <Check size={16} />
                    ) : (
                      <Copy size={16} />
                    )}
                  </IconButton>
                )}
                {/* Stripe cancel/resume only — Apple/Google are managed in their store (use the
                    recovery menu's force-expire to revoke a store-managed sub locally). */}
                {(!subscription.provider || subscription.provider === "stripe") &&
                  subscription.status === "active" && (
                    <IconButton
                      variant="ghost"
                      size="1"
                      onClick={() => handleCancel(subscription)}
                      disabled={cancelSubscription.isPending}
                      title="Cancel Subscription"
                      color="orange"
                    >
                      <Pause size={16} />
                    </IconButton>
                  )}
                {(!subscription.provider || subscription.provider === "stripe") &&
                  subscription.status === "canceled" && (
                    <IconButton
                      variant="ghost"
                      size="1"
                      onClick={() => handleResume(subscription)}
                      disabled={resumeSubscription.isPending}
                      title="Resume Subscription"
                      color="green"
                    >
                      <Play size={16} />
                    </IconButton>
                  )}
                <DropdownMenu
                  icon={
                    <IconButton variant="ghost" size="1" title="Recovery actions">
                      <Wrench size={16} />
                    </IconButton>
                  }
                  items={[
                    {
                      label: "Re-sync from provider",
                      icon: <RefreshCw size={14} />,
                      onClick: () => handleResync(subscription),
                    },
                    {
                      label: "Relink / manage users",
                      icon: <UserCog size={14} />,
                      onClick: () => handleViewDetails(subscription),
                    },
                    {
                      label: "Force-expire",
                      icon: <Ban size={14} />,
                      onClick: () => handleForceExpire(subscription),
                    },
                    {
                      label: "Delete subscription",
                      icon: <Trash2 size={14} />,
                      onClick: () => handleAdminDelete(subscription),
                    },
                  ]}
                />
              </Flex>
            )}
            actionsWidth={200}
          />
        )}
      </Flex>
        </Tabs.Content>
      </Tabs.Root>
    </PageTemplate>
  );
}
